// workLog — records HOW the work was done (docs/provenance-spec.md).
//
// The public claim promises employers "graded, tamper-proof work". Grading is
// server-side already; this is the sequence of actions that produced the
// artefact, which is the part no competitor can produce — the work happens in
// a builder we own, and the code is a derivative of the flow rather than the
// artefact, so there is no answer to paste.
//
// Three rules this file exists to keep:
//
//   1. It records STRUCTURAL ACTIONS on the student's own work and nothing
//      else. No keystrokes, no clipboard contents, no screen, no camera, no
//      tab focus. `editor.paste` carries a LENGTH, never the text.
//   2. It never breaks the app. Provenance is evidence, not a feature the
//      learner is waiting on: every failure path here is a silent no-op.
//   3. It does not decide anything. Order, timestamps and hashes are assigned
//      by the server; this is a reporter, not a witness we trust.

import { getAuthToken } from './projectService';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FLUSH_MS = 5000;
const FLUSH_AT = 25;
const MAX_BUFFER = 500; // a runaway loop must not eat memory

// How long an event may wait for a session before it is no longer plausibly
// part of the work that session records. Long enough to cover a mount race and
// an effect re-run; short enough that a buffer left over from an earlier page
// can never be attributed to an unrelated item.
const PENDING_TTL_MS = 60000;

let sessionId = null;
let studentId = null;
let sessionKey = null;   // `${studentId}:${projectId}` — what the open session is FOR
let buffer = [];
let timer = null;
let heartbeat = null;
let flushing = false;

// keepalive lets a request outlive the page, so the last batch still lands when
// the tab is closed. Browsers cap a keepalive body at 64 KB, so it is used for
// event batches only, never for a checkpoint (a whole flow).
const KEEPALIVE_MAX = 60000;

// The server takes the student from the bearer token, never from the body.
// `student_id` is still sent so a backend deployed before that keeps accepting
// the request; the current one ignores it. The token is read from the session
// cache the AuthProvider keeps (synchronously, not via authFetch): nothing may
// sit between pagehide and the keepalive fetch, or the last batch is lost.
const post = async (path, body, { keepalive = false } = {}) => {
  const json = JSON.stringify(body);
  const token = getAuthToken();
  const res = await fetch(`${API_URL}/api/work${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: json,
    keepalive: keepalive && json.length < KEEPALIVE_MAX,
  });
  if (!res.ok) {
    const err = new Error(`work log ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

// Open a session for an attempt.
//
// Re-opening for the SAME student and project is a no-op that returns the
// session already running. React re-runs the mounting effect whenever a
// dependency settles, and each restart previously orphaned the session before
// it: a real build produced two sessions seventeen seconds apart, split the
// evidence between them, and dropped everything logged across the seam.
export async function startWorkSession({ studentId: sid, practiceItemId, practiceItemSlug, assessmentItemId, projectId } = {}) {
  if (!sid) return null;
  const key = `${sid}:${projectId || ''}:${practiceItemId || practiceItemSlug || ''}:${assessmentItemId || ''}`;
  if (sessionId && sessionKey === key) return sessionId;
  try {
    const data = await post('/session', {
      student_id: sid,
      practice_item_id: practiceItemId || null,
      // The builder knows the item by slug only; the server resolves it, so the
      // session the REPAIR happens in is joinable to the item it was for.
      practice_item_slug: practiceItemSlug || null,
      assessment_item_id: assessmentItemId || null,
      project_id: projectId || null,
      // Narrow on purpose, and narrowed again server-side.
      client_meta: {
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
      },
    });
    sessionId = data.session_id;
    studentId = sid;
    sessionKey = key;
    // Adopt whatever was logged while this session was still opening, but only
    // if it is recent enough to belong to it. Anything older was some other
    // page's work and must not be attributed here.
    const cutoff = Date.now() - PENDING_TTL_MS;
    buffer = buffer.filter((e) => Date.parse(e.client_at) >= cutoff);
    // Safety net. The per-push timer is the normal path, but anything that
    // clears it without draining — a failed flush, a race on the first event —
    // would otherwise strand the buffer until the next call. A heartbeat means
    // a buffered event is never more than FLUSH_MS from being sent.
    if (!heartbeat) heartbeat = setInterval(flushWork, FLUSH_MS);
    if (buffer.length) flushWork();
    return sessionId;
  } catch {
    sessionId = null; // evidence is best-effort; the lesson is not
    sessionKey = null;
    return null;
  }
}

// The session item.closed was last logged for: an item is closed once, whether
// the learner navigated away (the page's unmount) or closed the tab (pagehide).
let closedFor = null;

// Log that the learner left this item. Only into the open session, and only once
// per session: closing the tab used to leave no item.closed at all (#570), and the
// unmount and pagehide paths must not both write one.
export function logItemClosed(payload = {}) {
  if (!sessionId || closedFor === sessionId) return;
  closedFor = sessionId;
  logWork('item.closed', payload, { requireSession: true });
}

export function isRecording() {
  return Boolean(sessionId);
}

// Record one action. Fire-and-forget by design — nothing awaits this.
//
// Deliberately NOT gated on an open session. The session POST is a round trip,
// and the builder logs `item.opened` and can add nodes before it returns; the
// mounting effect may also restart, leaving a window with no session at all.
// Gating here silently discarded exactly the actions that prove the flow was
// built rather than pasted. Events wait in the buffer instead, and flushWork
// sends them once there is a session to hang them on.
//
// `requireSession` is the exception: an event that only means something inside
// THIS item's session (a graded result, a hint) is dropped when none is open,
// rather than waiting to be adopted by whatever item opens next (#569). Without
// it, an answer on a lesson page landed in the next builder item's evidence.
export function logWork(kind, payload = {}, { requireSession = false } = {}) {
  if (!kind) return;
  if (requireSession && !sessionId) return;
  if (buffer.length >= MAX_BUFFER) return;
  buffer.push({ kind, payload, client_at: new Date().toISOString() });
  if (buffer.length >= FLUSH_AT) {
    flushWork();
  } else if (!timer) {
    timer = setTimeout(flushWork, FLUSH_MS);
  }
}

export async function flushWork() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (flushing || !sessionId || buffer.length === 0) return;
  flushing = true;
  const batch = buffer;
  buffer = [];
  try {
    await post(`/${sessionId}/events`, { student_id: studentId, events: batch }, { keepalive: true });
  } catch (err) {
    // A 4xx is a verdict on the batch itself — an event kind this server does
    // not know, a payload it will not take. Retrying cannot change it, and the
    // frontend and backend deploy separately, so a version skew would wedge the
    // buffer on a poison batch and cost every event after it. Drop it and keep
    // recording. Anything else (offline, 5xx) is worth retrying, so put it back.
    if (!(err && err.status >= 400 && err.status < 500)) {
      buffer = [...batch, ...buffer].slice(0, MAX_BUFFER);
    }
  } finally {
    flushing = false;
  }
}

// Post the current flow so the SERVER can diff it against the last snapshot.
// The client asserts nothing here — it hands over a flow, and the backend
// works out what changed and timestamps it with its own clock. An unchanged
// post is a no-op server-side, so a caller cannot inflate the timeline by
// posting on a timer with nothing happening.
export async function checkpointFlow({ flowId, flowData } = {}) {
  if (!sessionId || !flowData) return null;
  try {
    return await post(`/${sessionId}/checkpoint`, {
      student_id: studentId,
      flow_id: flowId || null,
      flow_data: flowData,
    });
  } catch {
    return null;   // evidence is best-effort; the build is not
  }
}

export function endWorkSession() {
  flushWork();
  if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
  sessionId = null;
  studentId = null;
  sessionKey = null;
}

// Best-effort flush when the tab goes away. `visibilitychange` + keepalive is
// the only reliable moment; `unload` is not.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushWork();
  });
}
// A closed tab never unmounts the page, so the page's own item.closed never ran.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    logItemClosed({ left: 'tab_closed' });
    flushWork();
  });
}

// Test seam only — the module is otherwise deliberately stateful, because a
// session is a real-world thing that outlives any one component.
export function __resetWorkLog() {
  sessionId = null;
  studentId = null;
  sessionKey = null;
  closedFor = null;
  buffer = [];
  if (timer) clearTimeout(timer);
  timer = null;
  if (heartbeat) clearInterval(heartbeat);
  heartbeat = null;
  flushing = false;
}
